import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { Action, select, Store } from '@ngrx/store';
import { ROUTER_NAVIGATED, RouterReducerState } from '@ngrx/router-store';
import {
  filter,
  ignoreElements,
  map,
  pluck,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import isObject from 'lodash.isobject';
import isEqual from 'lodash.isequal';

import { SortOptionsEnum, SortTypes } from '~shared/modules/application-event/models/sort-option.model';
import { SetSortWithoutNavigation, SortActionTypes } from './sort.actions';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { getRouterState } from '~core/store';
import * as fromBrandPLP from '~app/modules/brand-plp/store/selectors';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';

@Injectable({
  providedIn: 'root'
})
export class SortEffects {
  private activeRoute$ = this.store.pipe(select(getRouterState));
  private lastFilterParams$ = this.store.pipe(
    select(fromBrandPLP.getLastRequestFilterParameters)
  );

  constructor(
    private store: Store<RouterReducerState>,
    private router: Router,
    private actions$: Actions,
    private navigationHelper: NavigationHelper,
    private productsListUrlService: ProductsListUrlService,
  ) {
  }

  @Effect()
  updateNavigationOnChangeSort$: Observable<void> = this.actions$.pipe(
    ofType(SortActionTypes.SET_SORT),
    pluck('payload'),
    withLatestFrom(this.activeRoute$),
    map(([sortCode, routeState]) => [SortTypes.find(({ code }) => code === sortCode), routeState]),
    filter(([sortCode, routeState]) => isObject(sortCode) && !!routeState),
    tap(([{ key, value }, { queryParams } ]) =>
      this.navigationHelper.replaceUrl(
        [this.getCurrentPath()],
        { queryParams: { ...queryParams, orderBy: key, order: value } },
        { resetPaging: true }
      )
    ),
    ignoreElements()
  );

  @Effect()
  updateSortAfterNewRoute$: Observable<Action> = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    withLatestFrom(this.activeRoute$),
    filter(
      ([_, routeState]) => {
        if (!(routeState && routeState.url)) {
          return false;
        }

        return this.productsListUrlService.isProductListPage();
      }
    ),
    map(([_, { queryParams }]) => queryParams),
    filter(queryParams => queryParams['orderBy'] && queryParams['order']),
    switchMap(queryParams => {
      const { orderBy, order } = queryParams;

      const optionFromQuery = SortTypes.find(
        opt => opt.key === orderBy && opt.value === order
      );

      return of(new SetSortWithoutNavigation(optionFromQuery.code));
    })
  );

  @Effect()
  setDefaultSort: Observable<Action> = combineLatest([
    this.activeRoute$,
    this.lastFilterParams$
  ]).pipe(
    filter(([routeState, lastFilterParams]) => {
      if (!routeState) {
        return false;
      }

      const { queryParams } = routeState;

      if (!(lastFilterParams && queryParams)) {
        return false;
      }

      //NK if there is already sort in the url, we haven't change it
      if (queryParams.orderBy) {
        return false;
      }

      if (!queryParams.filter) {
        return true;
      }

      const sameTopPhrase = isEqual(
        lastFilterParams.top,
        queryParams.filter.top
      );
      const sameCategory = isEqual(
        lastFilterParams.categories,
        queryParams.filter.categories
      );

      return !(sameCategory && sameTopPhrase);
    }),
    switchMap(_ => {
      return of(new SetSortWithoutNavigation(SortOptionsEnum.BEST_MATCH));
    })
  );

  private getCurrentPath() {
    return this.router.url.split('?')[0].slice(1);
  }
}
